import React, { memo, useEffect, useState } from "react";
import MainPageDialog from "../../../../../components/dialog";
import { InputField } from "../../../../../components/reactHookFormFields/inputField";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/20/solid";
import { MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import { useAddPatientQuestionMutation } from "../../../../../services/chatService";

function AddProblemDialogue({
  showAddProblemDialogue = false,
  setShowAddProblemDialogue = () => {},
  selectedProblem = null,
  setSelectedProblem = () => {},
}) {
  const {
    handleSubmit,
    getValues,
    setFocus,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      questions: selectedProblem?.question_json || [],
      title: selectedProblem?.title || "",
    },
  });

  const [addingQuestion, setAddingQuestion] = useState(false);
  const [updateQuestionTrigger] = useAddPatientQuestionMutation();
  const [btnDisbaled, setBtnDisbaled] = useState(false);

  const handleCloseDialogue = () => {
    setShowAddProblemDialogue(false);
    setSelectedProblem(null);
    setValue("title", "");
    setValue("questions", []);
  };

  const footerlist = [
    {
      title: "Close",
      action: handleCloseDialogue,
      type: "cancel",
      disabled: addingQuestion,
      isDisabled: addingQuestion,
    },
    {
      title: selectedProblem ? "Update" : "Save",
      action: () => handleSubmit(onSubmitForm)(),
      type: "save",
      disabled: addingQuestion || btnDisbaled,
      isDisabled: addingQuestion || btnDisbaled,
      loading: addingQuestion,
    },
  ];

  const {
    fields: questionsArray,
    append: appendQuestion,
    remove: deleteQuestion,
  } = useFieldArray({
    control: control,
    name: "questions",
  });

  const onSubmitForm = async (values) => {

    if(btnDisbaled) {
      toast.error("Please fill all the fields");
      return;
    } 

    try {
      setAddingQuestion(true);
      const payload = {
        title: values.title,
        question_json: values.questions,
      };

      if (selectedProblem) {
        payload.id = selectedProblem.id;
      }

      await updateQuestionTrigger(payload)
        .then((response) => {
          const { code, msg } = response?.data?.response;
          if (code === 0 || code === "0") {
            toast.success(`Nursing Chat problem ${selectedProblem ? "updated" : "added"} successfully`);
            handleCloseDialogue();
          } else {
            // toast.error(msg || "Error while adding problem");
            console.log("Error while adding problem");
          }
        })
        .catch((error) => {
          console.error("Error while adding problem", error);
          toast.error("Error while adding problem");
        })
        .finally(() => {
          setAddingQuestion(false);
          handleCloseDialogue();
        });
    } catch (error) {
      console.error("Error in adding problem", error);
      toast.error("Error in adding problem");
      setAddingQuestion(false);
    }
  };

  const watchQuestions = useWatch({
    control,
    name: "questions",
  });

  const watchTitle = useWatch({
    control,
    name: "title",
  });

  useEffect(() => {
    const length = watchQuestions.length;
    if (length === 0) {
      appendQuestion({
        text: "",
        type: "text",
      });
    }

    if (
      (length > 0 && watchQuestions[length - 1].text.trim() === "") ||
      !watchTitle.trim()
    ) {
      setBtnDisbaled(true);
    } else {
      setBtnDisbaled(false);
    }
  }, [watchQuestions, watchTitle]);

  useEffect(() => {
    const handleEnterKey = (event) => {
      if (event.key === "Enter" && !btnDisbaled && !addingQuestion) {
        event.preventDefault(); // Prevent form submission on Enter
        appendQuestion({
          text: "",
          type: "text",
        });
      }
    };

    // Add event listener for the Enter key
    window.addEventListener("keydown", handleEnterKey);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("keydown", handleEnterKey);
    };
  }, [btnDisbaled, addingQuestion, appendQuestion]);

  useEffect(() => {
    if(!getValues()["title"]) {
      setFocus("title");
    } else {
      setFocus(`questions[${questionsArray.length - 1}].text`);
    }
  }, [questionsArray]);

  return (
    <MainPageDialog
      title="Add Problem"
      footerList={footerlist}
      modalSize="md"
      maxHeight={true}
      onCancelHandler={handleCloseDialogue}
    >
      <div className="flex flex-col w-full my-3">
        <form
          id="add_problem_form"
          onSubmit={handleSubmit(onSubmitForm)}
          className="flex flex-col gap-2"
        >
          <div className="flex items-start w-[96%]">
            <InputField
              name="title"
              id="title"
              aria-label="Problem Title"
              title="Problem Title"
              control={control}
              errors={errors}
              autoComplete="off"
              rules={{required: { value: true }}}
            />
          </div>
          <div className="flex items-start p-2 w-full mb-1">
            <p className="text-md font-medium">Questions</p>
          </div>
          <div className="flex flex-col gap-4">
          {questionsArray?.map((question, index) => (
            <div className="flex items-center gap-4" key={question.id || index}>
              <InputField
                name={`questions[${index}].text`}
                id={`questions[${index}].text`}
                aria-label={`Question ${index + 1}`}
                title={`Question ${index + 1}`}
                control={control}
                errors={errors}
                autoComplete="off"
                rules={{required: { value: true }}}
              />
              <MdDelete
                size={18}
                color="red"
                className="cursor-pointer"
                title="Delete Question"
                aria-label="Delete Question"
                onClick={() => {
                  console.log("Deleting question at index:", index);
                  deleteQuestion(index);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    console.log("Deleting question at index (Enter):", index);
                    deleteQuestion(index);
                  }
                }}
              />
            </div>
          ))}
            <div>
              <button
                className={`flex whitespace-nowrap cursor-pointer bg-ethizo_primary items-center text-white pl-2 pr-3  py-2 rounded-md shadow-md hover:shadow-lg ${
                  btnDisbaled || addingQuestion ? "opacity-50" : ""
                }`}
                title="Add Problem"
                type="button"
                aria-label="Add Problem"
                disabled={btnDisbaled || addingQuestion}
                onClick={() =>
                  appendQuestion({
                    text: "",
                    type: "text",
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    appendQuestion({
                      text: "",
                      type: "text",
                    });
                  }
                }}
              >
                <PlusIcon className="h-5 w-5 cursor-pointer" /> Add Question
              </button>
            </div>
          </div>
        </form>
      </div>
    </MainPageDialog>
  );
}

export default memo(AddProblemDialogue);
